<script setup>
import { unref, defineProps, defineEmits, computed, watch, ref } from "vue";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import Caret from "@/components/shared/icons/Caret.vue";
import PlusIcon from "@/components/shared/icons/PlusIcon.vue";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import SearchableSelect from "@/components/shared/select/SearchableSelect.vue";
import ErrorMessage from "@/components/shared/ErrorMessage.vue";
import ConfiguratorWidgetChild from "./Configurator.vue";
import { useVModel } from "@vueuse/core";
import { baseEventSelectionRule } from "@/components/program-manager/sessions/constants";
import {
  configuratorColors,
  excursionConfiguratorConditionals,
} from "@/components/program-manager/sessions/constants";
import { uuid } from "vue-uuid";
import TrashCan from "@/components/shared/icons/TrashCan.vue";
import { helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";

const props = defineProps({
  modelValue: {
    type: Object,
  },
  selectOptions: {
    type: Array,
    default: [],
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
  depth: {
    type: Number,
    default: 1,
  },
});

const reachedMaxDepth = computed(() => {
  return props.depth > 1;
});

const emit = defineEmits(["update:modelValue"]);
const rule = useVModel(props, "modelValue", emit, { passive: true });

const validateSessionEventSelectionRules = (_value, sibling) => {
  if (sibling) {
    if (sibling?.conjunction === "or" && !sibling?.default_event_id) {
      return false;
    }
  }
  return true;
};

const validateSessionEventSelectionRulesPresent = (_value, sibling) => {
  if (sibling && !sibling?.rules?.length && !sibling?.libraries?.length) {
    return false;
  }
  return true;
};

const rules = {
  defaultIDRequired: {
    required: helpers.withMessage(
      "Default EXCURSION/EVENT is required",
      (_value, sibling) =>
        !validateSessionEventSelectionRulesPresent(_value, sibling)
          ? validateSessionEventSelectionRules(_value, sibling)
          : true
    ),
  },
  eventOrRuleRequired: {
    required: helpers.withMessage(
      "You must setup an EXCURSION/EVENT rule to save.",
      (_value, sibling) =>
        validateSessionEventSelectionRulesPresent(_value, sibling)
    ),
  },
};

const v$ = useVuelidate(rules, rule, {
  $registerAs: "Configurator",
  $lazy: true,
});

const addEvent = () => {
  unref(rule).libraries.push({ value: undefined });
};
const addGroup = () => {
  if (!reachedMaxDepth.value) {
    unref(rule).rules.push({
      value: { ...baseEventSelectionRule(), id: uuid.v4() },
    });
  }
};

const findNewDefaultId = (rule) => {
  const retVal = { defaultEventId: undefined, defaultRuleId: undefined };

  let foundDefaultEvent = false;
  let possibleEventDefault = undefined;
  let foundDefaultRule = false;
  let possibleRuleDefault = undefined;
  if (rule?.libraries?.length) {
    for (const library of rule.libraries) {
      if (library?.value?.id) {
        possibleEventDefault = library?.value?.id;
        if (rule?.default_event_id === library?.value?.id) {
          retVal.defaultEventId = library?.value?.id;
          foundDefaultEvent = true;
          break;
        }
      }
    }
  }

  if (!foundDefaultEvent && rule?.rules?.length) {
    for (const childRule of rule.rules) {
      if (childRule?.value?.id) {
        possibleRuleDefault = childRule?.value?.id;
        if (rule?.default_rule_id === childRule?.value?.id) {
          retVal.defaultRuleId = childRule?.value?.id;
          foundDefaultRule = true;
          break;
        }
      }
    }
  }

  if (!foundDefaultEvent && !foundDefaultRule) {
    if (possibleEventDefault) {
      retVal.defaultEventId = possibleEventDefault;
    } else if (possibleRuleDefault) {
      retVal.defaultRuleId = possibleRuleDefault;
    }
  }

  return retVal;
};

const enabled = ref(0);
watch(
  [() => rule.value.rules, () => rule.value.libraries],
  ([_newRules, newLibraries]) => {
    if (rule?.value?.conjunction === "or") {
      const possibleDefaults = findNewDefaultId(rule.value);
      if (possibleDefaults.defaultEventId) {
        rule.value.default_event_id = possibleDefaults.defaultEventId;
        rule.value.default_rule_id = undefined;
        rule.value.default_rule = undefined;
        enabled.value = 1;
      } else if (possibleDefaults.defaultRuleId) {
        rule.value.default_rule_id = possibleDefaults.defaultRuleId;
        rule.value.default_event_id = undefined;
        rule.value.default_event = undefined;
        enabled.value = 1;
      } else {
        rule.value.default_rule_id = undefined;
        rule.value.default_rule = undefined;
        rule.value.default_event_id = undefined;
        rule.value.default_event = undefined;
        enabled.value = 0;
      }
    } else {
      if (newLibraries?.length) {
        enabled.value = 1;
      } else {
        enabled.value = 0;
      }
    }
  },
  { deep: true, immediate: true }
);

watch(
  () => props.selectOptions,
  (updatedOptions) => {
    if (rule?.value?.libraries?.length > 0) {
      for (let i = rule?.value?.libraries?.length - 1; i >= 0; i--) {
        const library = rule?.value?.libraries[i];
        if (library?.value?.id) {
          const found = updatedOptions.find(
            (val) => val.id === library.value.id || val.event_id === library.value.id
          );
          if (!found) {
            rule?.value?.libraries.splice(i, 1);
          }
        }
      }
    }
  },
  { deep: true }
);

const showDefaults = ref(unref(rule).conjunction === "or");
watch(
  () => unref(rule).conjunction,
  (conjunction) => {
    if (conjunction === "or") {
      if (!rule.value.default_rule_id) {
        showDefaults.value = true;
        const lib = rule?.value?.libraries.find(
          (library) => !!library?.value?.id
        );
        rule.value.default_event_id = lib?.value?.id;
      }
    } else {
      showDefaults.value = false;
      rule.value.default_event_id = undefined;
      rule.value.default_event = undefined;
      rule.value.default_rule_id = undefined;
      rule.value.default_rule = undefined;
    }
  }
);

const remainingOptions = computed(() => {
  const usedEventMap = rule?.value?.libraries.reduce((acc, val) => {
    if (val?.value?.id) {
      acc[val.value.id] = true;
    }
    return acc;
  }, {});
  return props.selectOptions.filter((val) => !usedEventMap[val.id]);
});
</script>

<template>
  <div class="flex flex-col col-span-2 w-full">
    <div v-if="showLabel" class="block text-xs font-semibold text-gray-700">
      Configurator
    </div>
    <div class="flex w-full">
      <div
        class="w-[110px] m-h-[220px] split-color flex flex-column justify-center items-center"
      >
        <Listbox v-model="rule.conjunction">
          <div>
            <ListboxButton
              :style="{
                'background-color':
                  configuratorColors[depth % 2].middle[enabled],
              }"
              class="relative w-20 h-[34px] bg-blue-800 cursor-pointer rounded-full text-white py-2 pl-3 text-center uppercase flex items-center justify-around"
            >
              <div>{{ rule.conjunction }}</div>
              <Caret class="w-[13px] mr-3 white-caret" />
            </ListboxButton>
            <ListboxOptions
              class="absolute z-10 mt-1 max-h-60 w-20 overflow-auto rounded-md bg-white py-1 text-base text-black shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm cursor-pointer"
            >
              <ListboxOption
                v-for="conditional in excursionConfiguratorConditionals"
                :key="conditional.id"
                v-slot="{ active, selected }"
                as="template"
                :value="conditional.id"
              >
                <li
                  :class="['relative select-none py-2 text-center uppercase']"
                >
                  <span :class="[selected ? 'font-semibold' : 'font-normal']">{{
                    conditional.name
                  }}</span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </div>
        </Listbox>
      </div>
      <div
        class="w-full bg-blue-425 pl-2.5"
        :style="{ 'background-color': configuratorColors[depth % 2].right }"
      >
        <div class="grid grid-cols-12 gap-x-2 gap-y-4 my-4 ml-12">
          <label
            name="Track Name"
            class="common-label-text col-span-2 text-left my-auto"
            >Track Name</label
          >
          <BasicTextInput
            v-model="rule.name"
            outer-classes="col-span-3 flex items-center"
            field-label-classes="common-label-text w-[134px] text-nowrap"
          />
          <div class="common-label-text col-span-1 text-center my-auto">
            {{ showDefaults ? "Default" : "" }}
          </div>
          <div class="col-span-6"></div>
          <template v-for="(library, index) in rule?.libraries" :key="index">
            <SearchableSelect
              v-model="library.value"
              :options="remainingOptions"
              class="col-span-5"
              placeholder="Search and select an EXCURSION/EVENT"
              label="title"
              scroll-input="hidden"
            >
              <template #option="{ title, location }">
                <div class="text-sm">
                  <div class="font-semibold whitespace-normal">{{ title }}</div>
                  <div>{{ location }}</div>
                </div>
              </template>
            </SearchableSelect>
            <div v-if="showDefaults" class="flex items-center col-span-1">
              <input
                type="radio"
                class="h-4 w-4 border-gray-400 form-radio mx-auto"
                :checked="
                  rule.default_event_id &&
                  rule.default_event_id === library?.value?.id
                "
                :disabled="!library?.value?.id"
                @input="
                  () => {
                    rule.default_event_id = library?.value?.id;
                    rule.default_rule_id = undefined;
                  }
                "
              />
            </div>
            <TrashCan
              :class="[showDefaults ? 'col-span-5' : 'col-span-4']"
              class="col-span-4 cursor-pointer my-auto"
              @click="() => rule?.libraries?.splice(index, 1)"
            />
          </template>
        </div>
        <ErrorMessage
          error-classes="common-error-text ml-12 mb-4"
          :vuelidate-instance="v$?.defaultIDRequired"
        />
        <div
          v-for="(childRule, index) in rule.rules"
          class="mb-2 mr-8 flex align-baseline items-center w-full"
        >
          <ConfiguratorWidgetChild
            v-model="childRule.value"
            :select-options="props.selectOptions"
            :depth="props.depth + 1"
          />
          <div v-if="showDefaults" class="flex items-center col-span-1">
            <input
              type="radio"
              class="h-4 w-4 ml-2 border-gray-400 form-radio mx-auto"
              :checked="
                rule.default_rule_id &&
                rule.default_rule_id === childRule?.value?.id
              "
              @input="
                () => {
                  rule.default_event_id = undefined;
                  rule.default_rule_id = childRule?.value?.id;
                }
              "
            />
          </div>
          <TrashCan
            class="w-[18px] ml-2 mr-6 cursor-pointer"
            @click="() => rule.rules.splice(index, 1)"
          />
        </div>
        <div class="flex mb-2 pl-12">
          <div
            class="flex text-teal-900 items-center mr-6 text-xs font-semibold antialiased cursor-pointer uppercase"
            @click="addEvent"
          >
            <PlusIcon class="mr-1 w-6 h-6" />
            <div>Excursion/Event</div>
          </div>
          <div
            v-if="!reachedMaxDepth"
            class="flex text-teal-900 items-center text-xs font-semibold antialiased cursor-pointer uppercase"
            @click="addGroup"
          >
            <PlusIcon class="mr-1 w-6 h-6" />
            <div>Group</div>
          </div>
        </div>
      </div>
    </div>
    <ErrorMessage
      class="mt-2"
      error-classes="common-error-text ml-12 mb-4"
      :vuelidate-instance="v$?.eventOrRuleRequired"
    />
  </div>
</template>

<style scoped>
.split-color {
  background: linear-gradient(
    to right,
    v-bind(configuratorColors[depth % 2].left) 48%,
    v-bind(configuratorColors[depth % 2].middle[enabled]) 48% 51%,
    v-bind(configuratorColors[depth % 2].right) 51%
  );
}
.white-caret path {
  fill: white;
}
</style>
